import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  Home,
  Visibility,
  EventBusy,
  People,
  Source,
  Business,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import {
  TabPanel,
  StyledTabs,
  KPICard,
  SectionHeader,
  ChartCard,
  DataTable,
  StatusChip,
  COLORS,
  CHART_COLORS,
  PageContainer,
} from './components/ReportShared';
import DateRangeFilter, { getDefaultDateRange } from './components/DateRangeFilter';
import useReportData from './hooks/useReportData';
import {
  fetchSVOverall,
  fetchSVConversion,
  fetchSVNoShowReschedule,
  fetchSVAgentEffectiveness,
  fetchSVSourceQuality,
  fetchSVProjectReport,
} from '../../services/reportV2Service';

const PIE_COLORS = [CHART_COLORS[0], CHART_COLORS[4], CHART_COLORS[2]];

const LoadingOverlay = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
    <CircularProgress />
  </Box>
);

const ErrorMessage = ({ error }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
    <Typography color="error">{error}</Typography>
  </Box>
);

export default function SiteVisitIntelligence() {
  const [tabValue, setTabValue] = useState(0);
  const [overallDateRange, setOverallDateRange] = useState(getDefaultDateRange());
  const [conversionDateRange, setConversionDateRange] = useState(getDefaultDateRange());
  const [noShowDateRange, setNoShowDateRange] = useState(getDefaultDateRange());
  const [agentEffectivenessDateRange, setAgentEffectivenessDateRange] = useState(getDefaultDateRange());
  const [sourceVisitQualityDateRange, setSourceVisitQualityDateRange] = useState(getDefaultDateRange());
  const [projectVisitDateRange, setProjectVisitDateRange] = useState(getDefaultDateRange());

  const handleTabChange = (_, newValue) => setTabValue(newValue);

  const overall = useReportData(fetchSVOverall, overallDateRange, {}, tabValue === 0);
  const conversion = useReportData(fetchSVConversion, conversionDateRange, {}, tabValue === 1);
  const noShow = useReportData(fetchSVNoShowReschedule, noShowDateRange, {}, tabValue === 2);
  const agentEff = useReportData(fetchSVAgentEffectiveness, agentEffectivenessDateRange, {}, tabValue === 3);
  const sourceQuality = useReportData(fetchSVSourceQuality, sourceVisitQualityDateRange, {}, tabValue === 4);
  const projectReport = useReportData(fetchSVProjectReport, projectVisitDateRange, {}, tabValue === 5);

  const TABS = [
    'Overall Site Visit Report',
    'Visit-to-Booking Conversion',
    'No-Show & Reschedule Rate',
    'Agent-wise Visit Effectiveness',
    'Source-wise Visit Quality',
    'Project-wise Visit Report',
  ];

  const ok = overall.data?.kpis || {};
  const ck = conversion.data?.kpis || {};
  const nk = noShow.data?.kpis || {};
  const ak = agentEff.data?.kpis || {};
  const sk = sourceQuality.data?.kpis || {};
  const pk = projectReport.data?.kpis || {};

  return (
    <PageContainer>
      <StyledTabs value={tabValue} onChange={handleTabChange} tabs={TABS} />

      {/* Tab 1: Overall Site Visit Report */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Overall Site Visit Report
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Scheduled vs completed outcomes for selected period
            </Typography>
          </Box>
          <DateRangeFilter value={overallDateRange} onChange={setOverallDateRange} />
        </Box>
        {overall.loading ? <LoadingOverlay /> : overall.error ? <ErrorMessage error={overall.error} /> : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard title="Total Visits" value={ok.totalVisits ?? '-'} icon={Visibility} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard title="Completed" value={ok.completed ?? '-'} icon={Visibility} color={COLORS.green} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard title="Cancelled" value={ok.cancelled ?? '-'} icon={EventBusy} color={COLORS.red} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard title="No-Show" value={ok.noShow ?? '-'} icon={EventBusy} color={COLORS.orange} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ChartCard title="Daily Visits Trend" height={320}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={overall.data?.dailyTrend || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="scheduled" stroke={CHART_COLORS[0]} strokeWidth={2} name="Scheduled" dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="completed" stroke={CHART_COLORS[1]} strokeWidth={2} name="Completed" dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="cancelled" stroke={CHART_COLORS[4]} strokeWidth={2} name="Cancelled" dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="noShow" stroke={CHART_COLORS[5]} strokeWidth={2} name="No-Show" dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SectionHeader title="Visit Summary by Date" subtitle="Date, scheduled, completed, cancelled, no-show and completion rate" />
              <DataTable
                columns={[
                  { label: 'Date', field: 'date' },
                  { label: 'Scheduled', field: 'scheduled', align: 'right' },
                  { label: 'Completed', field: 'completed', align: 'right' },
                  { label: 'Cancelled', field: 'cancelled', align: 'right' },
                  { label: 'No-Show', field: 'noShow', align: 'right' },
                  { label: 'Completion Rate %', field: 'completionRate', align: 'right', render: (v) => `${v}%` },
                ]}
                rows={overall.data?.table || []}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Tab 2: Visit-to-Booking Conversion */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Visit-to-Booking Conversion
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Visits vs bookings outcomes for selected period
            </Typography>
          </Box>
          <DateRangeFilter value={conversionDateRange} onChange={setConversionDateRange} />
        </Box>
        {conversion.loading ? <LoadingOverlay /> : conversion.error ? <ErrorMessage error={conversion.error} /> : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard title="Completed Visits" value={ck.completedVisits ?? '-'} icon={Visibility} color={COLORS.green} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard title="Bookings" value={ck.bookings ?? '-'} icon={Home} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard title="Visit-to-Booking Rate" value={ck.v2bRate != null ? `${ck.v2bRate}%` : '-'} icon={Visibility} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard title="Avg Days to Book after Visit" value={ck.avgDaysToBook ?? '-'} icon={EventBusy} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="Visits vs Bookings by Month" height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={conversion.data?.monthlyTable || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="visits" fill={CHART_COLORS[0]} name="Visits" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="bookings" fill={CHART_COLORS[1]} name="Bookings" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="Visits & Conversion Rate by Month" height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={conversion.data?.monthlyTable || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v, name) => [name === 'conversion' ? `${v}%` : v, name === 'conversion' ? 'Conversion %' : 'Visits']} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="visits" fill={CHART_COLORS[0]} name="Visits" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="conversion" stroke={CHART_COLORS[2]} strokeWidth={2} name="Conversion %" dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SectionHeader title="Monthly Conversion Details" />
              <DataTable
                columns={[
                  { label: 'Month', field: 'month' },
                  { label: 'Visits', field: 'visits', align: 'right' },
                  { label: 'Bookings', field: 'bookings', align: 'right' },
                  { label: 'Conversion %', field: 'conversion', align: 'right', render: (v) => `${v}%` },
                  { label: 'Revenue', field: 'revenue', align: 'right' },
                ]}
                rows={conversion.data?.monthlyTable || []}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Tab 3: No-Show & Reschedule Rate */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              No-Show & Reschedule Rate
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              No-show and reschedule metrics for selected period
            </Typography>
          </Box>
          <DateRangeFilter value={noShowDateRange} onChange={setNoShowDateRange} />
        </Box>
        {noShow.loading ? <LoadingOverlay /> : noShow.error ? <ErrorMessage error={noShow.error} /> : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <KPICard title="No-Show Rate" value={nk.noShowRate != null ? `${nk.noShowRate}%` : '-'} icon={EventBusy} color={COLORS.red} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <KPICard title="Reschedule Rate" value={nk.rescheduleRate != null ? `${nk.rescheduleRate}%` : '-'} icon={EventBusy} color={COLORS.orange} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <KPICard title="Total No-Shows" value={nk.totalNoShows ?? '-'} icon={EventBusy} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <KPICard title="Total Rescheduled" value={nk.totalRescheduled ?? '-'} icon={EventBusy} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="Visit Outcome Distribution" height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={noShow.data?.outcomeDistribution || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, value }) => `${name}: ${value}%`}
                    >
                      {(noShow.data?.outcomeDistribution || []).map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `${v}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="No-Show Rate by Day of Week" height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={noShow.data?.noshowByDay || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v, name) => [name === 'noShowRate' ? `${v}%` : v, name === 'noShowRate' ? 'No-Show Rate' : 'Visits']} />
                    <Legend />
                    <Bar dataKey="noShowRate" fill={CHART_COLORS[4]} name="No-Show Rate %" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SectionHeader title="Agent-wise No-Show & Reschedule" />
              <DataTable
                columns={[
                  { label: 'Agent', field: 'agent' },
                  { label: 'Visits Scheduled', field: 'scheduled', align: 'right' },
                  { label: 'Completed', field: 'completed', align: 'right' },
                  { label: 'No-Shows', field: 'noShows', align: 'right' },
                  { label: 'Rescheduled', field: 'rescheduled', align: 'right' },
                  { label: 'No-Show %', field: 'noShowRate', align: 'right', render: (v) => `${v}%` },
                  { label: 'Reschedule %', field: 'rescheduleRate', align: 'right', render: (v) => `${v}%` },
                ]}
                rows={noShow.data?.agentTable || []}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Tab 4: Agent-wise Visit Effectiveness */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Agent-wise Visit Effectiveness
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Completed vs scheduled outcomes by agent for selected period
            </Typography>
          </Box>
          <DateRangeFilter value={agentEffectivenessDateRange} onChange={setAgentEffectivenessDateRange} />
        </Box>
        {agentEff.loading ? <LoadingOverlay /> : agentEff.error ? <ErrorMessage error={agentEff.error} /> : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <KPICard title="Most Effective Agent" value={ak.bestAgent || '-'} icon={People} color={COLORS.green} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <KPICard title="Least Effective" value={ak.worstAgent || '-'} icon={People} color={COLORS.red} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <KPICard title="Avg Effectiveness" value={ak.avgEffectiveness || '-'} icon={People} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <KPICard title="Avg Visit Duration" value={ak.avgDuration || '-'} icon={Visibility} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ChartCard title="Agent Effectiveness %" height={320}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={agentEff.data?.table || []} layout="vertical" margin={{ top: 10, right: 20, left: 80, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="agent" width={80} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, name) => [name === 'conversion' ? `${v}%` : name === 'avgDuration' ? `${v} min` : v, name === 'conversion' ? 'Effectiveness %' : name === 'avgDuration' ? 'Avg Duration' : name]} />
                    <Bar dataKey="conversion" fill={COLORS.teal[600]} name="Effectiveness %" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SectionHeader title="Agent-wise Visit Details" />
              <DataTable
                columns={[
                  { label: 'Agent', field: 'agent' },
                  { label: 'Total Visits', field: 'totalVisits', align: 'right' },
                  { label: 'Completed', field: 'completed', align: 'right' },
                  { label: 'Conversion %', field: 'conversion', align: 'right', render: (v) => `${v}%` },
                  { label: 'Avg Duration', field: 'avgDuration', align: 'right', render: (v) => `${v} min` },
                  { label: 'Feedback Score', field: 'feedbackScore', align: 'right' },
                ]}
                rows={agentEff.data?.table || []}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Tab 5: Source-wise Visit Quality */}
      <TabPanel value={tabValue} index={4}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Source-wise Visit Quality
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Lead quality outcomes by source for selected period
            </Typography>
          </Box>
          <DateRangeFilter value={sourceVisitQualityDateRange} onChange={setSourceVisitQualityDateRange} />
        </Box>
        {sourceQuality.loading ? <LoadingOverlay /> : sourceQuality.error ? <ErrorMessage error={sourceQuality.error} /> : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <KPICard title="Best Source" value={sk.bestSource || '-'} icon={Source} color={COLORS.green} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <KPICard title="Worst Source" value={sk.worstSource || '-'} icon={Source} color={COLORS.red} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <KPICard title="Avg V2B" value={sk.avgV2B || '-'} icon={Source} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ChartCard title="Visits & Visit-to-Booking Rate by Source" height={320}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={sourceQuality.data?.table || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis dataKey="source" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v, name) => [name === 'v2bRate' ? `${v}%` : v, name === 'v2bRate' ? 'V2B Rate %' : 'Visits']} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="visits" fill={CHART_COLORS[0]} name="Visits" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="v2bRate" fill={CHART_COLORS[2]} name="V2B Rate %" radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SectionHeader title="Source-wise Visit Quality Details" />
              <DataTable
                columns={[
                  { label: 'Source', field: 'source' },
                  { label: 'Leads', field: 'leads', align: 'right' },
                  { label: 'Visits', field: 'visits', align: 'right' },
                  { label: 'Bookings', field: 'bookings', align: 'right' },
                  { label: 'Visit Rate %', field: 'visitRate', align: 'right', render: (v) => `${v}%` },
                  { label: 'V2B Rate %', field: 'v2bRate', align: 'right', render: (v) => `${v}%` },
                ]}
                rows={sourceQuality.data?.table || []}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Tab 6: Project-wise Visit Report */}
      <TabPanel value={tabValue} index={5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Project-wise Visit Report
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Completed and cancelled visits by project for selected period
            </Typography>
          </Box>
          <DateRangeFilter value={projectVisitDateRange} onChange={setProjectVisitDateRange} />
        </Box>
        {projectReport.loading ? <LoadingOverlay /> : projectReport.error ? <ErrorMessage error={projectReport.error} /> : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <KPICard title="Most Visited" value={pk.mostVisited || '-'} icon={Business} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <KPICard title="Least Visited" value={pk.leastVisited || '-'} icon={Business} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <KPICard title="Avg Visits/Project" value={pk.avgVisitsPerProject || '-'} icon={Business} color={COLORS.teal[600]} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ChartCard title="Completed vs Cancelled by Project" height={320}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={projectReport.data?.table || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis dataKey="project" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="completed" stackId="a" fill={CHART_COLORS[0]} name="Completed" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="cancelled" stackId="a" fill={CHART_COLORS[4]} name="Cancelled" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SectionHeader title="Project-wise Visit Details" />
              <DataTable
                columns={[
                  { label: 'Project', field: 'project' },
                  { label: 'Total Visits', field: 'totalVisits', align: 'right' },
                  { label: 'Completed', field: 'completed', align: 'right' },
                  { label: 'Cancelled', field: 'cancelled', align: 'right' },
                  { label: 'Bookings', field: 'bookings', align: 'right' },
                  { label: 'Conversion %', field: 'conversion', align: 'right', render: (v) => `${v}%` },
                  { label: 'Avg Duration', field: 'avgDuration', align: 'right', render: (v) => `${v} min` },
                ]}
                rows={projectReport.data?.table || []}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>
    </PageContainer>
  );
}
